// ./src/components/features/Budget/BudgetPaymentDrawer/BudgetPaymentDrawer.tsx
//
// Create/edit form for ONE payment toward a budget item. Saves immediately via
// BudgetPaymentService and calls onSaved so the parent list + item amounts
// refresh. Fields: amount, paid on (date), method, who paid (polymorphic party),
// note. Stacked on top of the payments drawer.

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Stack, Textarea } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";

import { useResponsiveDatePickerProps } from "@/components/generic/form";
import { OpResult } from "@sdflc/api-helpers";

import { BudgetPayment, BudgetPaymentInput, BudgetPaymentService } from "@/utils/api";
import { getFormErrors, formatErrors, getTodayDate } from "@/utils";
import { useModalShortcuts, useFormSnapshot, SHORTCUT_HINTS } from "@/hooks";
import { AppDrawer, AppIcon, SelectField } from "@/components/generic";
import { ResponsiblePartySelect } from "@/components/features/Tasks/ResponsiblePartySelect/ResponsiblePartySelect";

import { MoneyInput } from "../MoneyInput/MoneyInput";
import { PAYMENT_METHOD_META } from "../budgetHelpers";

const KNOWN_FORM_FIELDS = ["amount", "paidAt", "paymentMethod", "note", "paidByKind", "paidById"];

export interface BudgetPaymentDrawerProps {
  opened: boolean;
  onClose: () => void;
  /** The budget item this payment belongs to — set on CREATE. */
  budgetItemId: string;
  /** The event, for the "who paid" party picker. */
  eventId: string;
  currencyCode?: string | null;
  /** The payment being edited, or null to create. */
  payment?: BudgetPayment | null;
  onSaved: () => void;
}

interface FormData {
  amount: number | null;
  paidAt: string | null;
  paymentMethod: string | null;
  note: string;
  paidByKind: string | null;
  paidById: string | null;
}

// A new payment defaults "Paid on" to today (local calendar date, tz-safe).
const getInitialFormData = (): FormData => ({
  amount: null,
  paidAt: getTodayDate(),
  paymentMethod: null,
  note: "",
  paidByKind: null,
  paidById: null,
});

const fromRecord = (record: BudgetPayment): FormData => ({
  amount: record.amount ?? null,
  paidAt: record.paidAt || null,
  paymentMethod: record.paymentMethod || null,
  note: record.note || "",
  paidByKind: record.paidByKind || null,
  paidById: record.paidById || null,
});

export const BudgetPaymentDrawer: React.FC<BudgetPaymentDrawerProps> = ({
  opened,
  onClose,
  budgetItemId,
  eventId,
  currencyCode,
  payment,
  onSaved,
}) => {
  const isEditMode = Boolean(payment?.id);
  const responsiveDatePicker = useResponsiveDatePickerProps();

  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<OpResult | null>(null);

  // Esc / a click outside / Cancel ask before discarding a changed payment (#123). The seed includes the
  // "Paid on = today" default, so an untouched new payment is clean.
  const { isDirty, reseed, clear } = useFormSnapshot(formData);

  useEffect(() => {
    if (!opened) {
      clear();

      return;
    }

    // A refreshed payment keeps what the user has typed (#125); another payment replaces the form.
    const seeded = payment ? fromRecord(payment) : getInitialFormData();
    setFormData(reseed(seeded, payment?.id ?? "__new__"));
    setError(null);
  }, [opened, payment, clear, reseed]);

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(error, KNOWN_FORM_FIELDS),
    [error]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  const setField = useCallback((field: keyof FormData, value: FormData[keyof FormData]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handlePaidByChange = useCallback((kind: string | null, id: string | null) => {
    setFormData((prev) => ({ ...prev, paidByKind: kind, paidById: id }));
  }, []);

  const handleSubmit = useCallback(async () => {
    setError(null);

    const submitData: BudgetPaymentInput = {
      amount: formData.amount ?? 0,
      paidAt: formData.paidAt || null,
      paymentMethod: formData.paymentMethod,
      note: formData.note.trim() || null,
      paidByKind: formData.paidByKind,
      paidById: formData.paidById,
    };

    setSaving(true);

    let result: OpResult;
    if (isEditMode && payment?.id) {
      result = await BudgetPaymentService.update({ id: payment.id }, submitData);
    } else {
      result = await BudgetPaymentService.create({ ...submitData, budgetItemId });
    }

    setSaving(false);

    if (result.didSucceed()) {
      onSaved();
      onClose();
    } else {
      setError(result);
    }
  }, [formData, isEditMode, payment, budgetItemId, onSaved, onClose]);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !saving,
    enabled: opened,
  });

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={isEditMode ? "Edit payment" : "Add payment"}
      position="right"
      size="md"
      closable={!saving}
      loading={saving}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      isDirty={isDirty}
    >
      <Stack gap="md">
        <MoneyInput
          label="Amount"
          value={formData.amount}
          onChange={(v) => setField("amount", v)}
          currency={currencyCode}
          error={fieldErrors.amount}
          required
          data-autofocus
        />

        <DatePickerInput
          label="Paid on"
          placeholder="Pick a date"
          value={formData.paidAt || null}
          onChange={(v) => setField("paidAt", (v as unknown as string) || null)}
          valueFormat="MMM DD, YYYY"
          error={fieldErrors.paidAt}
          clearable
          size="md"
          {...responsiveDatePicker}
        />

        <SelectField
          label="Method"
          placeholder="How was it paid?"
          data={PAYMENT_METHOD_META.map((m) => ({ value: m.value, label: m.label }))}
          value={formData.paymentMethod}
          onChange={(_name, v) => setField("paymentMethod", v)}
          leftSection={<AppIcon iconName="Wallet" size={16} />}
          error={fieldErrors.paymentMethod}
          clearable
          size="md"
        />

        <ResponsiblePartySelect
          eventId={eventId}
          value={{ kind: formData.paidByKind, id: formData.paidById }}
          onChange={handlePaidByChange}
          fallbackLabel={payment?.paidByLabel}
          label="Who paid"
          kindError={fieldErrors.paidByKind}
          idError={fieldErrors.paidById}
        />

        <Textarea
          label="Note"
          placeholder="Reference, invoice #, details…"
          value={formData.note}
          onChange={(e) => setField("note", e.currentTarget.value)}
          error={fieldErrors.note}
          autosize
          minRows={2}
          maxRows={5}
          size="md"
        />
      </Stack>
    </AppDrawer>
  );
};

export default BudgetPaymentDrawer;
